import { isValid } from 'date-fns';
import {
  date as formatDate,
  DEFAULT_LOCALE,
  getIntl,
  time,
} from '@frameio/i18n';
import TEXT from './generated/TEXT/GetFormattedDate';

TEXT.DEFINE({
  /** Formats the date with month, day, year, and then includes the time if returnBothDateAndTime is true (i.e. Nov 25, 2020 at 7:50 PM or just Nov 25, 2020) */
  formatDateAndTime: ({
    returnBothDateAndTime,
    date,
  }: {
    returnBothDateAndTime: boolean;
    date: Date;
  }): string => {
    return `${formatDate(date, 'medium')}${
      returnBothDateAndTime ? ` at ${time(date, 'short')}` : ''
    }`;
  },
  /** Formats the time only with the short format (i.e. 7:50 PM) */
  formatTimeOnly: (date: Date): string => {
    return `${time(date, 'short')}`;
  },
});

type FormattedDateOptions = {
  /**
   * The date to format. When not a valid date empty strings will be returned.
   */
  date: Date;
  /**
   * Returns a unique `formattedDate` key with the date formatted as follow: "Nov 20, 2020 at 8:40 PM".
   * Otherwise, returns the following keys: `formattedDay`, `formattedMonth`, `formattedYear`, `formattedTime`.
   */
  returnDateParts?: boolean;
  /**
   * Whether to include the time in the formatted date.
   * i.e. Nov 25, 2020 at 7:50pm or Nov 25, 2020
   */
  returnBothDateAndTime?: boolean;
};

const defaultFormatedDateOptions = {
  returnDateParts: false,
  returnBothDateAndTime: true,
};

type FormattedDateObj = {
  // when date parts are returned
  formattedDay?: string;
  formattedMonth?: string;
  formattedYear?: string;
  formattedTime?: string;

  // when only the formatted date is returned
  formattedDate?: string;
};

/**
 * This takes in a date and returns an object with either a formatted date key or with day, month, year and time keys.
 * When `returnDateParts` option is `false`, `getFormattedDate` returns an object with a unique
 * `formattedDate` key (i.e. "Nov 20, 2020 at 8:40 PM").
 * When `returnDateParts` option is `true`, `getFormattedDate` returns an object with the following keys:
 * `formattedDay`, `formattedMonth`, `formattedYear`, `formattedTime`.
 */
export const getFormattedDate = (
  options: FormattedDateOptions
): FormattedDateObj => {
  const {
    date: dateValue,
    returnDateParts,
    returnBothDateAndTime,
  } = {
    ...defaultFormatedDateOptions,
    ...options,
  };

  if (!isValid(dateValue)) {
    if (returnDateParts) {
      return {
        formattedDay: '',
        formattedMonth: '',
        formattedYear: '',
        formattedTime: '',
      };
    }
    return {
      formattedDate: '',
    };
  }

  const locale = getIntl()?.locale ?? DEFAULT_LOCALE;

  const dateParts = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).formatToParts(dateValue);

  const { day, month, year } = Object.fromEntries(
    dateParts.map((part) => [part.type, part.value])
  );

  return returnDateParts
    ? {
        formattedDay: day,
        formattedMonth: month,
        formattedYear: year,
        formattedTime: TEXT.formatTimeOnly(dateValue),
      }
    : {
        formattedDate: TEXT.formatDateAndTime({
          returnBothDateAndTime,
          date: dateValue,
        }),
      };
};
